import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full rounded-none border-white/15 bg-zinc-900 text-white placeholder:text-zinc-600 focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary h-12";

function errText(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("vd_token", data.token);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-background px-6">
      <div className="glow-teal left-1/4 top-1/4 h-[380px] w-[380px] bg-teal-500/15" />
      <div className="glow-teal bottom-1/4 right-1/4 h-[340px] w-[340px] bg-purple-500/15" />

      <button
        data-testid="back-home"
        onClick={() => nav("/")}
        className="absolute left-6 top-6 flex items-center gap-2 font-mono-x text-xs uppercase tracking-widest text-zinc-500 transition-colors hover:text-white"
      >
        <ArrowLeft size={15} /> Back to site
      </button>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-card p-8 md:p-10"
      >
        <div className="mb-8 flex items-center gap-2.5">
          <span className="h-2.5 w-2.5 rotate-45 gradient-fill" />
          <span className="font-cabinet text-lg font-black uppercase tracking-tight text-white">Velocity<span className="gradient-text">.</span></span>
        </div>

        <div className="mb-8 flex items-center gap-3">
          <Lock size={18} className="text-primary" />
          <h1 className="font-cabinet text-2xl font-black uppercase tracking-tight text-white">Owner Login</h1>
        </div>

        <form onSubmit={submit} data-testid="login-form" className="space-y-5">
          <div className="space-y-2">
            <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={field} />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={field} />
          </div>
          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 gradient-fill px-8 py-4 font-mono-x text-xs uppercase tracking-widest text-black transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
